"use client";

import { ReactNode } from "react";
import { useLanguage } from "../../context/LanguageContext";
import { BulletItem } from "../BulletItem";
import { Header } from "../Header";
import { PageTitle } from "../PageTitle";
import SiteFooter from "../SiteFooter";
import { bodyTextClass, cardHeadingClass } from "../typography";

type CardProps = {
  title: string;
  children: ReactNode;
};

function InfoCard({ title, children }: CardProps): JSX.Element {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <h2 className={cardHeadingClass}>{title}</h2>
      <div className={`mt-3 ${bodyTextClass}`}>{children}</div>
    </div>
  );
}

export default function PrivacyPage(): JSX.Element {
  const { t } = useLanguage();
  const c = t.privacyPage;
  const contactEmail = t.common.footer.email;

  return (
    <div className="flex min-h-screen flex-col" dir={t.common.dir} lang={t.common.lang}>
      <Header />
      <main className="flex-1">
        <section className="scroll-mt-28 py-16">
          <div className="mx-auto max-w-5xl px-4">
            <PageTitle
              label={c.label}
              title={c.title}
              intro={c.intro}
              lastUpdated={c.lastUpdated}
            />

            <div className="grid gap-6">
              <InfoCard title={c.collectTitle}>
                <ul className="list-none space-y-2">
                  {c.collectList.map((item) => (
                    <BulletItem key={item} text={item} textClassName={bodyTextClass} />
                  ))}
                </ul>
              </InfoCard>

              <InfoCard title={c.whyTitle}>
                <ul className="list-none space-y-2">
                  {c.whyList.map((item) => (
                    <BulletItem key={item} text={item} textClassName={bodyTextClass} />
                  ))}
                </ul>
              </InfoCard>

              <InfoCard title={c.useTitle}>
                <ul className="list-none space-y-2">
                  {c.useList.map((item) => (
                    <BulletItem key={item} text={item} textClassName={bodyTextClass} />
                  ))}
                </ul>
              </InfoCard>

              <InfoCard title={c.sharingTitle}>
                <ul className="list-none space-y-2">
                  {c.sharingList.map((item) => (
                    <BulletItem key={item} text={item} textClassName={bodyTextClass} />
                  ))}
                </ul>
              </InfoCard>

              <InfoCard title={c.cookiesTitle}>
                <p className="text-start rtl:text-right">{c.cookiesBody}</p>
              </InfoCard>

              <InfoCard title={c.contactTitle}>
                <p className="text-start rtl:text-right">
                  {c.contactBody}{" "}
                  <a
                    href={`mailto:${contactEmail}`}
                    className="text-sky-700 hover:text-sky-800 dark:text-sky-400 dark:hover:text-sky-300"
                  >
                    {contactEmail}
                  </a>
                  .
                </p>
              </InfoCard>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
